import { useState } from 'react';
import { t } from '@aquabox/shared';
import { api, ApiError } from '../api/client';
import { Icon } from '../components/Icon';

/**
 * Sayfa zemininde tekrarlı filigran deseni (§3.1). Prototipteki 96×96 SVG ile birebir
 * (docs/prototip/index.html, login bölümü). Renk data URI içine gömülür çünkü CSS
 * custom property (`--navy-900`) bir `background-image` data URI'sinin içinde çözülmez.
 */
const LOGIN_WATERMARK =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'%3E%3Cpath d='M8 88V8h56v40H36v20h32' fill='none' stroke='%2314285a' stroke-width='7'/%3E%3C/svg%3E\")";

/** Giriş ekranı (§3.1). Şoförler mobil uygulamadan girer; burada yalnız panel rolleri. */
export function Login({ onSuccess, notice }: { onSuccess: () => void; notice?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    void doLogin();
  }

  async function doLogin(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.login(email, password);
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('error.internal'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: '100vh',
        background: 'var(--page-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: 16,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: LOGIN_WATERMARK,
          backgroundRepeat: 'repeat',
          opacity: 0.06,
          pointerEvents: 'none',
        }}
      />
      <form
        onSubmit={submit}
        className="card"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 420,
          padding: 36,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <svg width="44" height="44" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <path
              d="M16 4h8v6l9 6v15a4 4 0 0 1-4 4H11a4 4 0 0 1-4-4V16l9-6z"
              fill="var(--primary)"
            />
            <rect
              x="12"
              y="20"
              width="4"
              height="10"
              rx="2"
              fill="var(--primary-light)"
              opacity="0.6"
            />
          </svg>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              fontWeight: 600,
              color: 'var(--navy-900)',
            }}
          >
            {t('common.appName')}
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
            {t('panel.loginTagline')}
          </div>
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 600,
            lineHeight: 1.2,
            textAlign: 'center',
          }}
        >
          {t('panel.loginTitle')}
        </h2>

        <div>
          <label className="label" htmlFor="email">
            {t('auth.email')}
          </label>
          <input
            id="email"
            className="input"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="label" htmlFor="password">
            {t('auth.password')}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingRight: 44 }}
            />
            {/* Göz ikonu `Icon.tsx`te zaten vardı, burada bir "●" karakteri duruyordu: ne
                parolayı gösterdiğini anlatıyor ne de dokunulacak kadar büyüktü. */}
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
              style={{
                position: 'absolute',
                right: 4,
                top: 0,
                height: 48,
                width: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: showPassword ? 'var(--primary)' : 'var(--text-secondary)',
              }}
            >
              <Icon name="eye" size={18} />
            </button>
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: 'var(--primary)' }}
          />
          {t('panel.rememberMe')}
        </label>

        {notice && !error && (
          <div
            role="status"
            style={{
              background: 'var(--primary-bg)',
              border: '1px solid var(--primary-light)',
              color: 'var(--primary)',
              borderRadius: 'var(--r-input)',
              padding: '10px 14px',
              fontSize: 13.5,
              fontWeight: 500,
            }}
          >
            {notice}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: 'var(--bg-danger-soft)',
              border: '1px solid var(--danger)',
              color: 'var(--danger)',
              borderRadius: 'var(--r-input)',
              padding: '10px 14px',
              fontSize: 13.5,
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          className={`btn btn-primary ${busy ? 'btn-loading' : ''}`}
          disabled={busy}
          style={{ height: 52, justifyContent: 'center' }}
        >
          {busy && <span className="spinner" />}
          {busy ? t('panel.loggingIn') : t('auth.login')}
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: '1px solid var(--primary-light)',
            background: 'var(--primary-bg)',
            borderRadius: 'var(--r-input)',
            padding: '12px 14px',
            marginTop: 6,
          }}
        >
          <span style={{ color: 'var(--primary)', flex: '0 0 17px' }}>
            <Icon name="info" size={17} strokeWidth={1.7} />
          </span>
          <span style={{ fontSize: 13.5, fontWeight: 500 }}>{t('panel.driversUseMobile')}</span>
        </div>

        {/* Tıklanabilir değil: sıfırlama bağlantısını patron/sistem yöneticisi üretir. */}
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {t('panel.loginForgotHelp')}
        </p>
      </form>
    </div>
  );
}
